/**
 * Operations Hub (docs/specs/portal/operations-hub.md §14 to §20): the map of every project of a tenant with its
 * project list, modelled on MB's prototype v3 (`docs/reference/prototypes/thomson-operations-hub-prototype-v3.html`).
 * Built on the existing rules, nothing repeated:
 *
 * - progress: `opsProgress` (the Figures tab, the Progress page and the Job Summary);
 * - block boundaries: `blockFeatures`; lines: `lineStateFeatures` (decision 372, as Teslyn Field's map);
 * - report words: `staffReportLabel` (the hub is staff's, so it says which revision; R139).
 *
 * New here: the project's aircraft and aircraft type (Q367 A: colours by aircraft type, the prototype's), the pin at
 * the centre of the boundaries, the contract's survey areas for a project without a flight plan yet (Q368 A), when
 * lines are drawn, the status words (Q366 open) and the latest report. Not built yet, waiting for MB: tracking assets
 * (Q362, Q363), estimated finish (Q364) and crew (Q365); see spec §19.
 */
import type { Block, IsoDate, IsoDateTime, LineStatus, PlannedLine, Project, Report, Uuid } from './model.js';
import {
  blockFeatures,
  type BlockOutline,
  type LayerResult,
  type LegendEntry,
  type MapFeature,
} from './map-layers.js';
import { boundsOf, type GeoBounds } from './map-geo.js';
import { contractAreaRing } from './project-area.js';
import {
  kmPerDay,
  opsProgress,
  type ContractArea,
  type OpsLine,
  type OpsProgress,
  type VariationKm,
} from './ops.js';
import { formatKm } from './km.js';
import type { LatLng } from './planning.js';
import { periodText, staffReportLabel } from './portal.js';
import type { QuoteArea } from './quote-model.js';
import type { AircraftPosition, TrailPoint } from './aircraft-layer.js';
import { flightStatus } from './flight-status.js';
import {
  FLEET_PROVIDERS,
  aircraftColours,
  fleetGroupsOf,
  onePerAircraft,
  showFleetGroups,
  toAircraftPosition,
  validFix,
  type AircraftColours,
  type FleetProviderId,
  type TrackedPosition,
  type TrackerAsset,
  type TrackingSettings,
} from './tracker.js';
import { trackingList, type TrackingList } from './tracking-list.js';
import {
  CLOUD_TRAFFIC_FRESH_MS,
  CLOUD_TRAFFIC_TRAIL_MS,
  cloudTrafficCredits,
  type PublicTrafficHubRow,
} from './cloud-traffic.js';
import { publicTrailPoint } from './public-traffic.js';

// ---------------------------------------------------------------- aircraft type and colour (§16.1, §16.2)

/** The prototype's four aircraft types (Q367 A). */
export type HubAircraftType = 'c210' | 'c208' | 'helicopter' | 'other';

/** Filter and legend order, as the prototype's select. */
export const HUB_AIRCRAFT_TYPES: readonly HubAircraftType[] = ['c210', 'c208', 'helicopter', 'other'];

export const HUB_AIRCRAFT_TYPE_LABEL: Readonly<Record<HubAircraftType, string>> = {
  c210: 'Cessna 210',
  c208: 'Cessna 208',
  helicopter: 'Helicopter',
  other: 'Other',
};

/** The Hub's map colour tokens (`brand/tokens/tokens.json` group `mapHub`), never set by a tenant. */
export type HubColour = 'map-hub-c210' | 'map-hub-c208' | 'map-hub-helicopter' | 'map-hub-other';

/** Every Hub colour token, in `tokens.json` order (a test keeps the two equal). */
export const HUB_COLOUR_TOKENS: readonly HubColour[] = [
  'map-hub-c210',
  'map-hub-c208',
  'map-hub-helicopter',
  'map-hub-other',
];

/**
 * The one table that colours a project (Q367 A, Claude's recommended answer): Cessna 210 blue, Cessna 208 purple,
 * helicopter green, other grey, the prototype's colours. If MB answers otherwise (B: by status), change `hubColourOf`
 * and the tokens; nothing else colours a project.
 */
export const HUB_TYPE_COLOUR: Readonly<Record<HubAircraftType, HubColour>> = {
  c210: 'map-hub-c210',
  c208: 'map-hub-c208',
  helicopter: 'map-hub-helicopter',
  other: 'map-hub-other',
};

const HELICOPTER =
  /helicop|\bheli\b|robinson|\br(22|44|66)\b|\bas ?3[35]0|squirrel|\bbell\b|\bec ?1[23]\d\b|\bh1[23]\d\b|eurocopter|hughes|\bmd ?[56]\d\d\b|\bbk ?117\b/i;
const C208 = /208|caravan/i;
const C210 = /210|centurion/i;

/** The aircraft type from a type name ("Cessna Grand Caravan" is a Cessna 208); "other" when unknown or empty. */
export function hubAircraftType(typeName: string | null | undefined): HubAircraftType {
  const t = (typeName ?? '').trim();
  if (t === '') return 'other';
  if (HELICOPTER.test(t)) return 'helicopter';
  if (C208.test(t)) return 'c208';
  if (C210.test(t)) return 'c210';
  return 'other';
}

/** An aircraft that flew the project, as `ops_hub_projects` returns it. */
export interface HubFlownAircraft {
  readonly aircraft_id: Uuid | null;
  readonly rego: string;
  readonly last_date: IsoDate;
  readonly flights: number;
}

/** An aircraft of the tenant's fleet (`aircraft` table). */
export interface HubFleetAircraft {
  readonly id: Uuid;
  readonly registration: string;
  readonly type_name: string;
}

export interface HubProjectAircraft {
  /** Registrations, the latest flown first. */
  readonly regos: readonly string[];
  readonly typeName: string;
  readonly type: HubAircraftType;
  /** Where it was read: the flights, the signed contract, the project's job type, or nowhere. */
  readonly source: 'flights' | 'contract' | 'job_type' | 'none';
  /** "VH-JOZ Cessna Grand Caravan", "Cessna 210", "Aircraft not recorded". */
  readonly text: string;
}

const norm = (r: string): string => r.trim().toUpperCase();

/**
 * The aircraft of a project (§16.1): the aircraft that flew it, latest first (type from the fleet); else the signed
 * contract's registrations (type from the fleet, else the contract's type); else the contract's type; else
 * "Helicopter" for a helicopter job; else not recorded.
 */
export function hubProjectAircraft(input: {
  readonly flown: readonly HubFlownAircraft[];
  readonly contractType: string | null;
  readonly contractRegos: readonly string[];
  readonly jobType: Project['job_type'];
  readonly fleet: readonly HubFleetAircraft[];
}): HubProjectAircraft {
  const byId = new Map(input.fleet.map((a) => [a.id, a]));
  const byRego = new Map(input.fleet.map((a) => [norm(a.registration), a]));
  const contractType = (input.contractType ?? '').trim();
  const make = (
    regos: readonly string[],
    typeName: string,
    source: HubProjectAircraft['source'],
  ): HubProjectAircraft => ({
    regos,
    typeName,
    type: typeName === '' && input.jobType === 'helicopter' ? 'helicopter' : hubAircraftType(typeName),
    source,
    text: [regos.join(', '), typeName].filter((s) => s !== '').join(' ') || 'Aircraft not recorded',
  });

  const flown = [...input.flown]
    .filter((f) => f.rego.trim() !== '' || f.aircraft_id !== null)
    .sort((a, b) => b.last_date.localeCompare(a.last_date) || b.flights - a.flights);
  if (flown.length > 0) {
    const fleetOf = (f: HubFlownAircraft): HubFleetAircraft | undefined =>
      (f.aircraft_id ? byId.get(f.aircraft_id) : undefined) ?? byRego.get(norm(f.rego));
    const regos = [
      ...new Set(flown.map((f) => norm(f.rego) || fleetOf(f)?.registration || '').filter(Boolean)),
    ];
    const typeName =
      flown.map((f) => fleetOf(f)?.type_name?.trim() ?? '').find((t) => t !== '') ?? contractType;
    return make(regos, typeName, 'flights');
  }
  const regos = [...new Set(input.contractRegos.map(norm).filter(Boolean))];
  if (regos.length > 0 || contractType !== '') {
    const typeName =
      regos.map((r) => byRego.get(r)?.type_name?.trim() ?? '').find((t) => t !== '') ?? contractType;
    return make(regos, typeName, 'contract');
  }
  if (input.jobType === 'helicopter') return make([], 'Helicopter', 'job_type');
  return make([], '', 'none');
}

/** A project's colour on the map and its card (§16.2): its aircraft type's. */
export function hubColourOf(aircraft: Pick<HubProjectAircraft, 'type'>): HubColour {
  return HUB_TYPE_COLOUR[aircraft.type];
}

// ---------------------------------------------------------------- status, reports (§16.6, §16.7)

/** A project status, Mobilising included (decision 423, built on `p4/project-lifecycle`). */
export type HubStatus = Project['status'] | 'mobilising';

/** The statuses the Hub shows, in order: every project that is not archived. */
export const HUB_STATUSES: readonly HubStatus[] = ['planned', 'mobilising', 'active', 'complete'];

/** Status words (decision 423, Q366 B): Planned is shown as Contracted, Active as In progress. */
export const HUB_STATUS_LABEL: Readonly<Record<HubStatus, string>> = {
  planned: 'Contracted',
  mobilising: 'Mobilising',
  active: 'In progress',
  complete: 'Complete',
  archived: 'Archived',
};

/** The Hub shows every project that is not archived. */
export function hubShows(status: string): boolean {
  return status !== 'archived';
}

/** A live report of the project, as `ops_hub_projects` returns it. */
export type HubReportRef = Pick<Report, 'kind' | 'period' | 'revision'> & {
  readonly generated_at: IsoDateTime;
};

/** The report generated last, in the Reports page's staff words ("Daily report, 16 Oct 2026 (revision 2)"); null with none. */
export function hubLatestReport(reports: readonly HubReportRef[]): string | null {
  const latest = [...reports].sort(
    (a, b) =>
      b.generated_at.localeCompare(a.generated_at) ||
      b.period.localeCompare(a.period) ||
      b.revision - a.revision,
  )[0];
  return latest ? staffReportLabel(latest) : null;
}

// ---------------------------------------------------------------- estimated finish (decision 421)

/** The window of the estimate: the last 14 calendar days, today included. */
export const HUB_FINISH_WINDOW_DAYS = 14;

export type FinishConfidence = 'low' | 'preliminary' | 'good';

export const FINISH_CONFIDENCE_LABEL: Readonly<Record<FinishConfidence, string>> = {
  low: 'Low',
  preliminary: 'Preliminary',
  good: 'Good',
};

/** Decision 421: Low under 5 days of data, Preliminary 5 to 9, Good 10 or more. */
export function finishConfidence(days: number): FinishConfidence {
  return days < 5 ? 'low' : days < 10 ? 'preliminary' : 'good';
}

export interface HubFinish {
  /** The first day of the window: 13 days before today, or the project's first day if later. */
  readonly from: IsoDate | null;
  /** Calendar days of data in the window, standby and weather days included. */
  readonly days: number;
  /** Accepted km per calendar day over the window; null before the project's first day. */
  readonly kmPerDay: number | null;
  readonly finish: IsoDate | null;
  readonly confidence: FinishConfidence;
  /** "Estimated finish 31 Oct 2026 · Preliminary (9 days of data, 1,088.78 km a day)", or why there is none. */
  readonly text: string;
}

const DAY_MS = 86_400_000;
const dayMs = (d: IsoDate): number => Date.parse(`${d}T00:00:00Z`);
const isoDay = (ms: number): IsoDate => new Date(ms).toISOString().slice(0, 10);

/**
 * The estimated finish of a project (decision 421, Q364 A): the km remaining ÷ the accepted km per **calendar** day
 * over the last 14 days (weather and standby days count, so it is realistic), each line counted on the day it was
 * flown (`kmPerDay`, the Figures rule). The window starts no earlier than the project's first day (its first daily
 * entry or flight), so a new project's rate is not diluted by days before it began; the confidence word says how many
 * days the rate rests on.
 */
export function hubEstimatedFinish(input: {
  /** The line statuses with their flight's day (`ops_line_status`), of the window at least. */
  readonly lines: readonly OpsLine[];
  readonly projectId: Uuid;
  /** The project's first daily entry or flight; null when there is none yet. */
  readonly firstDay: IsoDate | null;
  /** The viewer's local date. */
  readonly today: IsoDate;
  readonly remainingKm: number;
  /**
   * Days left out of the window (decision 491): the dates of daily entries marked Suspended and every day of the
   * project's stand-down period (`standDownDays`). The rate is the km over the days that remain.
   */
  readonly standDownDays?: readonly IsoDate[];
  /**
   * Blocks ticked "Job km are completed" (N32, `OpsProgress.kmCompletedBlocks`): with nothing left to fly because of
   * the tick, the words say so rather than "Every planned line is accepted".
   */
  readonly kmCompletedBlocks?: number;
}): HubFinish {
  const none = (
    text: string,
    from: IsoDate | null = null,
    days = 0,
    rate: number | null = null,
  ): HubFinish => ({
    from,
    days,
    kmPerDay: rate,
    finish: null,
    confidence: finishConfidence(days),
    text,
  });
  if (input.remainingKm <= 0)
    return none(
      (input.kmCompletedBlocks ?? 0) > 0
        ? 'Nothing left to fly: Job km are completed on every block'
        : 'Every planned line is accepted',
    );
  if (!input.firstDay || input.firstDay > input.today) return none('No estimated finish yet: nothing flown');
  const end = dayMs(input.today);
  const from = isoDay(Math.max(end - (HUB_FINISH_WINDOW_DAYS - 1) * DAY_MS, dayMs(input.firstDay)));
  const window = Math.round((end - dayMs(from)) / DAY_MS) + 1;
  const left = new Set((input.standDownDays ?? []).filter((d) => d >= from && d <= input.today));
  const days = window - left.size;
  const standDown =
    left.size > 0 ? `, ${left.size} stand-down ${left.size === 1 ? 'day' : 'days'} left out` : '';
  if (days <= 0) return none(`No estimated finish: the last ${window} days are a stand-down`, from, 0, null);
  const accepted = kmPerDay(input.lines, {
    from,
    to: input.today,
    projectId: input.projectId,
    aircraftId: null,
  })
    .filter((d) => !left.has(d.date))
    .reduce((a, d) => a + d.acceptedKm, 0);
  const rate = accepted / days;
  if (!(rate > 0))
    return none(`No estimated finish: no accepted km in the last ${window} days${standDown}`, from, days, 0);
  const finish = isoDay(end + Math.ceil(input.remainingKm / rate) * DAY_MS);
  const confidence = finishConfidence(days);
  return {
    from,
    days,
    kmPerDay: rate,
    finish,
    confidence,
    text: `Estimated finish ${periodText(finish)} · ${FINISH_CONFIDENCE_LABEL[confidence]} (${days} ${days === 1 ? 'day' : 'days'} of data${standDown}, ${formatKm(rate)} km a day)`,
  };
}

/** Every day of a stand-down period (`YYYY-MM-DD`, inclusive); none without both dates or when they are reversed. */
export function standDownDays(from: IsoDate | null | undefined, to: IsoDate | null | undefined): IsoDate[] {
  if (!from || !to || from > to) return [];
  const out: IsoDate[] = [];
  for (let t = dayMs(from); t <= dayMs(to) && out.length <= 400; t += DAY_MS) out.push(isoDay(t));
  return out;
}

// ---------------------------------------------------------------- outlines and pin (§16.3, §16.4)

/** The block columns the Hub draws (`ops_hub_projects` returns these). */
export type HubBlock = BlockOutline;

/**
 * Every block boundary of a project, outline only and without labels (§14), in the boundary blue of every other map
 * with its dark edge (decision 658: no longer the aircraft type's colour, which stays on the pin and progress bar).
 */
export function hubBoundaryFeatures(
  blocks: readonly HubBlock[],
  project: Pick<Project, 'epsg'>,
): LayerResult {
  const r = blockFeatures(blocks, project);
  return {
    features: r.features.map((f) => ({
      id: f.id,
      layer: f.layer,
      geometry: f.geometry,
      style: { ...f.style, fill: 0 },
      tooltip: f.tooltip,
      identify: f.identify,
    })),
    notDrawn: r.notDrawn,
  };
}

/** What a contract area needs to be drawn (the snapshot's `QuoteArea`). */
export type HubAreaShape = Pick<QuoteArea, 'name'> &
  Partial<Pick<QuoteArea, 'boundary' | 'zone' | 'hemisphere' | 'boundary_table'>>;

/** An area of the signed contract: its km (`ContractArea`) and its shape. */
export type HubContractArea = ContractArea & HubAreaShape;

/**
 * The survey areas of the signed contract (§16.4, Q368 A): for a project with no block boundaries yet. Each area's
 * grid boundary in its zone, else its printed table; WGS 84, GDA 94 and GDA2020 are drawn alike (under 2 m apart).
 * Dashed with a light fill, so they read as areas, not flown blocks, in the boundary blue with its dark edge (decision
 * 658: the aircraft type's colour only on the pin and progress bar).
 */
export function contractAreaFeatures(areas: readonly HubAreaShape[], key: string): LayerResult {
  const features: MapFeature[] = [];
  const notDrawn: string[] = [];
  areas.forEach((a, i) => {
    const ring = contractAreaRing(a);
    if (!ring || ring.some((p) => !Number.isFinite(p.lat) || !Number.isFinite(p.lng))) {
      notDrawn.push(`Contract area ${a.name}: no boundary recorded`);
      return;
    }
    features.push({
      id: `${key}:area:${i}`,
      layer: 'blocks',
      geometry: { type: 'polygon', rings: [ring] },
      style: { colour: 'map-line-boundary', stroke: 'normal', dashed: true, fill: 0.08, casing: true },
      tooltip: `Contract area ${a.name}`,
      identify: [{ label: 'Contract area', value: a.name }],
    });
  });
  return { features, notDrawn };
}

/** A contract area in the Hub's legend: the boundary blue dashed, as `contractAreaFeatures` draws it (decision 731). */
export const CONTRACT_AREA_LEGEND: LegendEntry = {
  colour: 'map-line-boundary',
  stroke: 'normal',
  dashed: true,
  word: 'Contract area (no blocks yet)',
};

/** Signed area (degrees²) and centroid of one ring (shoelace, longitude as x). */
function ringCentroid(ring: readonly LatLng[]): { area: number; at: LatLng } | null {
  if (ring.length < 3) return null;
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i]!;
    const q = ring[(i + 1) % ring.length]!;
    const cross = p.lng * q.lat - q.lng * p.lat;
    a += cross;
    cx += (p.lng + q.lng) * cross;
    cy += (p.lat + q.lat) * cross;
  }
  if (Math.abs(a) < 1e-12) return null;
  return { area: Math.abs(a / 2), at: { lat: cy / (3 * a), lng: cx / (3 * a) } };
}

/**
 * The pin of a project (§16.3): the area-weighted centre of its polygons; the centre of their box when no ring has
 * an area; null without any point.
 */
export function hubCentre(features: readonly MapFeature[]): LatLng | null {
  let w = 0;
  let lat = 0;
  let lng = 0;
  const points: LatLng[] = [];
  for (const f of features) {
    if (f.geometry.type !== 'polygon') continue;
    const outer = f.geometry.rings[0];
    if (!outer) continue;
    points.push(...outer);
    const c = ringCentroid(outer);
    if (!c) continue;
    w += c.area;
    lat += c.at.lat * c.area;
    lng += c.at.lng * c.area;
  }
  if (w > 0) return { lat: lat / w, lng: lng / w };
  const b = boundsOf(points);
  return b ? { lat: (b.south + b.north) / 2, lng: (b.west + b.east) / 2 } : null;
}

// ---------------------------------------------------------------- lines (§16.5, §18)

/** Lines are drawn from this zoom in (§16.5): below it a block of 50 m lines is a solid patch. */
export const HUB_LINES_MIN_ZOOM = 10;

const meets = (a: GeoBounds, b: GeoBounds): boolean =>
  a.west <= b.east && b.west <= a.east && a.south <= b.north && b.south <= a.north;

/** Whether a project's lines are drawn: zoomed in far enough and its box meets the view. */
export function hubShowsLines(zoom: number, box: GeoBounds | null, view: GeoBounds): boolean {
  return zoom >= HUB_LINES_MIN_ZOOM && box !== null && meets(box, view);
}

// ---------------------------------------------------------------- a project of the Hub

/** One project as `ops_hub_projects` returns it, in domain words. */
export interface HubProjectInput {
  readonly id: Uuid;
  readonly number: string;
  readonly title: string;
  readonly clientName: string;
  readonly status: string;
  /** The project's stand-down period (decision 491), `YYYY-MM-DD`; absent or null for none. */
  readonly standDownFrom?: IsoDate | null;
  readonly standDownTo?: IsoDate | null;
  /** Dates of the project's daily entries marked Suspended (the Hub's window at least). */
  readonly suspendedDays?: readonly IsoDate[];
  readonly jobType: Project['job_type'];
  readonly epsg: number | null;
  readonly blocks: readonly HubBlock[];
  /** The signed (or closed) contract's areas; null without one. */
  readonly contractAreas: readonly HubContractArea[] | null;
  readonly contractVariations: readonly VariationKm[];
  readonly contractAircraftType: string | null;
  readonly contractRegos: readonly string[];
  readonly flown: readonly HubFlownAircraft[];
  readonly reports: readonly HubReportRef[];
}

/** The plan and Line review of a project (`loadProjectPlan`), once loaded. */
export interface HubPlan {
  readonly blocks: readonly Block[];
  readonly plan: readonly PlannedLine[];
  readonly statuses: readonly LineStatus[];
}

export interface HubProject {
  readonly id: Uuid;
  readonly number: string;
  readonly title: string;
  readonly clientName: string;
  readonly status: string;
  readonly statusWord: string;
  /** "Benz Mining Corp · VH-JOZ Cessna Grand Caravan". */
  readonly subtitle: string;
  readonly aircraft: HubProjectAircraft;
  readonly colour: HubColour;
  readonly latestReport: string | null;
  /** Block boundaries, else the contract's areas (§14). */
  readonly outline: readonly MapFeature[];
  readonly outlineKind: 'blocks' | 'contract' | 'none';
  readonly notDrawn: readonly string[];
  readonly pin: LatLng | null;
  readonly box: GeoBounds | null;
  /** Null until the plan is loaded. */
  readonly progress: OpsProgress | null;
  /** Whether the project has planned lines; null until the plan is loaded. */
  readonly hasLines: boolean | null;
}

/** Everything the Hub shows of one project (§15, §16). `plan` is null while it loads. */
export function hubProject(
  p: HubProjectInput,
  fleet: readonly HubFleetAircraft[],
  plan: HubPlan | null,
): HubProject {
  const aircraft = hubProjectAircraft({
    flown: p.flown,
    contractType: p.contractAircraftType,
    contractRegos: p.contractRegos,
    jobType: p.jobType,
    fleet,
  });
  const colour = hubColourOf(aircraft);
  const blocks = hubBoundaryFeatures(p.blocks, { epsg: p.epsg });
  const areas =
    blocks.features.length === 0 && p.contractAreas ? contractAreaFeatures(p.contractAreas, p.id) : null;
  const outline = blocks.features.length > 0 ? blocks.features : (areas?.features ?? []);
  const outlineKind = blocks.features.length > 0 ? 'blocks' : outline.length > 0 ? 'contract' : 'none';
  const live = plan ? plan.plan.filter((l) => l.deleted_at === null) : null;
  return {
    id: p.id,
    number: p.number,
    title: p.title,
    clientName: p.clientName,
    status: p.status,
    statusWord: HUB_STATUS_LABEL[p.status as HubStatus] ?? p.status,
    subtitle: `${p.clientName || 'No client recorded'} · ${aircraft.text}`,
    aircraft,
    colour,
    latestReport: hubLatestReport(p.reports),
    outline,
    outlineKind,
    notDrawn: outlineKind === 'contract' ? (areas?.notDrawn ?? []) : blocks.notDrawn,
    pin: hubCentre(outline),
    box: boundsOf(outline.flatMap((f) => (f.geometry.type === 'polygon' ? f.geometry.rings.flat() : []))),
    // N32: the plan's blocks carry the "Job km are completed" tick
    progress: plan
      ? opsProgress(plan.statuses, plan.plan, p.contractAreas, p.contractVariations, plan.blocks)
      : null,
    hasLines: live ? live.length > 0 : null,
  };
}

/** The projects of one aircraft type, or all (the panel's filter scopes the list and the map). */
export function hubFilter(projects: readonly HubProject[], type: HubAircraftType | 'all'): HubProject[] {
  return projects.filter((p) => type === 'all' || p.aircraft.type === type);
}

// ---------------------------------------------------------------- tracking assets (decisions 419, 420)

/**
 * One fix as `ops_hub_positions` returns it: what the cloud poller stored (decision 419), for every device of the
 * organisation's tracking accounts (decision 453). The device key is the provider (`source`), the account label and
 * the provider's device id (`source_id`); `asset_id` is its tracker asset, null when it has none.
 */
export interface HubPositionRow {
  readonly asset_id: Uuid | null;
  readonly source: string;
  /** The integration's account label ('' for the only account of that provider; absent: ''). */
  readonly account_label?: string;
  readonly source_id: string;
  readonly label: string;
  /** What the tracker says or Teslyn suggests the device is (null or absent: the asset or fixed-wing decides). */
  readonly kind?: string | null;
  readonly fleet_group?: string;
  readonly registration?: string;
  readonly icao_hex?: string;
  readonly lat: number;
  readonly lon: number;
  readonly alt_ft: number | null;
  readonly speed_kt: number | null;
  readonly heading_deg: number | null;
  readonly fix_time: IsoDateTime;
  readonly airborne: boolean;
  /** True on a satellite link, false cellular, null or absent unknown. */
  readonly satellite?: boolean | null;
}

/** The key of one tracker device in the Hub: provider, account label and device id (`tracker_alert_state`'s key). */
export function hubDeviceKey(provider: string, accountLabel: string, deviceId: string): string {
  return `${provider}:${accountLabel}:${deviceId}`;
}

const HUB_KINDS: readonly string[] = ['fixed_wing', 'helicopter', 'ground'];
/** The public traffic sources Teslyn Cloud asks (parity C7; Oz Runways and AvPlan by decision 765). */
const HUB_PUBLIC_SOURCES = ['adsb', 'ozrunways', 'avplan'] as const;

export interface HubTracking {
  /** One per aircraft or vehicle, its latest fix, with its state (SOS, lost, stale, airborne, parked). */
  readonly aircraft: readonly AircraftPosition[];
  /** Trails by aircraft id, oldest first, within the history asked for. */
  readonly trails: ReadonlyMap<string, readonly TrailPoint[]>;
  readonly colours: ReadonlyMap<string, AircraftColours>;
  /** The list beside the map, as Teslyn Field's (`trackingList`). */
  readonly list: TrackingList;
  /** Every fleet group among the aircraft (before the group filter). */
  readonly groups: readonly string[];
  /** Public air traffic aircraft on the map (parity C7). */
  readonly publicCount: number;
  /** The credits of the public traffic services drawn ("Traffic: adsb.lol (ODbL)"). */
  readonly credits: readonly string[];
}

/**
 * The Hub's tracking assets (decision 420, Q363 A): the organisation's own trackers only, every device of their
 * accounts (decision 453: a device without a tracker asset too, named as its tracker names it; ground vehicles such
 * as phone trackers in a "Fieldcrew" group drawn as vehicles, hidden only when an asset hides them), as Teslyn
 * Field shows them, from the positions the cloud poller stored. Each stored fix becomes the common position model, then the
 * Field rules apply unchanged: one icon per aircraft (`onePerAircraft`), its state and colours (`toAircraftPosition`,
 * `aircraftColours`: grey when stale, decision 326), its last flight from the trail (`flightStatus`), the fleet
 * groups (`showFleetGroups`) and the list (`trackingList`).
 */
export function hubTracking(input: {
  readonly assets: readonly TrackerAsset[];
  readonly rows: readonly HubPositionRow[];
  /** Devices with an SOS active (`tracker_alert_state`), by `hubDeviceKey`. */
  readonly sos: ReadonlySet<string>;
  readonly settings: TrackingSettings;
  readonly nowMs: number;
  /** The viewer's UTC offset in hours, for the take-off and landing times. */
  readonly offsetH: number;
  /** Fleet groups ticked; none shows every aircraft. */
  readonly shownGroups?: readonly string[];
  /**
   * Public air traffic Teslyn Cloud asked around the active projects (parity C7, `ops_hub_public_traffic`): shown
   * with the trackers, one icon per aircraft (a tracked aircraft also seen on ADS-B shows once, from its tracker),
   * never in the list. As Teslyn Field draws it: an aircraft while its last fix is under 10 minutes old, its trail
   * the last 2 hours of its positions while moving (`publicTrailPoint`), no take-off or landing times.
   */
  readonly publicRows?: readonly PublicTrafficHubRow[];
}): HubTracking {
  const assets = input.assets.filter(
    (a) => a.deleted_at === null && (FLEET_PROVIDERS as readonly string[]).includes(a.provider),
  );
  // every device of the accounts, named or not (decision 453), as Teslyn Field shows it
  const points = new Map<string, TrackedPosition[]>();
  for (const r of input.rows) {
    const timeMs = Date.parse(r.fix_time);
    if (!(FLEET_PROVIDERS as readonly string[]).includes(r.source) || !Number.isFinite(timeMs)) continue;
    const account = r.account_label ?? '';
    const key = hubDeviceKey(r.source, account, r.source_id);
    const p: TrackedPosition = {
      source: r.source as FleetProviderId,
      account,
      deviceId: r.source_id,
      label: r.label,
      lat: r.lat,
      lon: r.lon,
      timeMs,
      altFt: r.alt_ft,
      speedKt: r.speed_kt,
      headingDeg: r.heading_deg,
      registration: r.registration || null,
      icaoHex: r.icao_hex || null,
      kind: r.kind && HUB_KINDS.includes(r.kind) ? (r.kind as TrackedPosition['kind']) : null,
      fleetGroup: r.fleet_group ?? '',
      sos: input.sos.has(key),
      satellite: r.satellite ?? null,
      providerFlying: r.airborne,
      tripId: null,
    };
    if (!validFix(p)) continue;
    const list = points.get(key);
    if (list) list.push(p);
    else points.set(key, [p]);
  }
  const publicKeys = new Set<string>();
  const usedPublic: PublicTrafficHubRow[] = [];
  for (const r of input.publicRows ?? []) {
    const timeMs = Date.parse(r.fix_time);
    const source = (HUB_PUBLIC_SOURCES as readonly string[]).includes(r.source)
      ? (r.source as (typeof HUB_PUBLIC_SOURCES)[number])
      : null;
    if (!source || !r.source_id || !Number.isFinite(timeMs)) continue;
    if (timeMs < input.nowMs - CLOUD_TRAFFIC_TRAIL_MS) continue;
    const key = hubDeviceKey(source, '', r.source_id);
    const p: TrackedPosition = {
      source,
      account: '',
      deviceId: r.source_id,
      label: r.label || r.registration || r.source_id.toUpperCase(),
      lat: r.lat,
      lon: r.lon,
      timeMs,
      altFt: r.alt_ft,
      speedKt: r.speed_kt,
      headingDeg: r.heading_deg,
      registration: r.registration || null,
      icaoHex: r.icao_hex || null,
      kind: r.kind && HUB_KINDS.includes(r.kind) ? (r.kind as TrackedPosition['kind']) : null,
      fleetGroup: '',
      sos: false,
      satellite: null,
      providerFlying: r.airborne,
      tripId: null,
    };
    if (!validFix(p)) continue;
    publicKeys.add(key);
    usedPublic.push(r);
    const list = points.get(key);
    if (list) list.push(p);
    else points.set(key, [p]);
  }
  for (const list of points.values()) list.sort((a, b) => a.timeMs - b.timeMs);
  // a public aircraft is drawn while its last fix is under 10 minutes old (it may have left the area or landed)
  for (const key of publicKeys) {
    const list = points.get(key)!;
    if (list[list.length - 1]!.timeMs < input.nowMs - CLOUD_TRAFFIC_FRESH_MS) points.delete(key);
  }
  const latest = [...points.values()].map((l) => l[l.length - 1]!);
  const groups = onePerAircraft(latest, assets, input.settings);
  const trails = new Map<string, TrailPoint[]>();
  const all: AircraftPosition[] = groups.map((g) => {
    const map = toAircraftPosition(g.position, g.asset, input.nowMs, input.settings, false);
    // a tracker's trail is every fix; a public aircraft's only its fixes while moving, as Teslyn Field keeps it
    const trail = g.members
      .flatMap((m) => {
        const key = hubDeviceKey(m.source, m.account, m.deviceId);
        const list = points.get(key) ?? [];
        return publicKeys.has(key) ? list.filter((p) => publicTrailPoint(p) !== null) : list;
      })
      .sort((a, b) => a.timeMs - b.timeMs);
    trails.set(
      map.id,
      trail.map((t) => ({
        lat: t.lat,
        lon: t.lon,
        timeMs: t.timeMs,
        altFt: t.altFt,
        speedKt: t.speedKt,
        headingDeg: t.headingDeg,
      })),
    );
    // take-off, flight time and landing for the organisation's own aircraft only (Teslyn Field's rule, 03-034)
    return { ...map, flight: map.fleet ? flightStatus(trail, map.kind, input.nowMs) : null };
  });
  const aircraft = showFleetGroups(all, input.shownGroups ?? []);
  const shownPublic = new Set(aircraft.filter((a) => a.fleet === false).map((a) => a.id));
  return {
    aircraft,
    trails,
    colours: aircraftColours(aircraft),
    list: trackingList(aircraft, input.nowMs, input.offsetH),
    groups: fleetGroupsOf(all),
    publicCount: shownPublic.size,
    credits:
      shownPublic.size > 0
        ? cloudTrafficCredits(usedPublic.filter((r) => points.has(hubDeviceKey(r.source, '', r.source_id))))
        : [],
  };
}
